import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import '../_messages.scss'
import { useTranslation } from 'react-i18next'
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'

import * as spriteIcons from 'assets/svg/sprite'
import MassMessagesModal from 'components/UI/Modal/MassMessagesModal/MassMessagesModal'
import { useHeaderLayout } from 'context/headerLayoutContext'
import { useModalContext } from 'context/modalContext'
import { usePageTitle } from 'context/pageTitleContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { AllIcons } from 'helpers/allIcons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { WhoAreYouWritingToFan } from 'pages/messaging/components/WhoAreYouWritingToFan'
import { getConversations, getQuickContacts } from 'services/endpoints/message'
import { useUserProfile } from 'store'
import { type InboxCacheEntry, useInboxCacheStore} from 'store/inboxCacheStore'
import { usePendingNotificationsStore } from 'store/pendingNotificationsStore'

import { ChatCard } from '../components/ChatCard'
import Placeholder from '../components/Placeholder'
import PlaceholderMobile from '../components/PlaceholderMobile'
import { PollAnswers } from '../components/PollAnswers'
import { QuickContact } from '../components/QuickContact'

type PublicId = string

interface IChatCard {
  last_message: string | null
  last_activity_at: string | null

  other_users: Array<{
    id: PublicId
    username: string | null
    display_name: string
    name: string
    avatar: {
      url: string | null
    }
    croppedAvatar: {
      url: string | null
    }
    is_premium: boolean
    deleted: boolean
    isSubscribed: boolean
    isBlocked: boolean
    isRestricted: boolean
    statistics?: {
      join_date: string | null
      expiring: string | null
      total_spent: number | null
    }
  }>

  oldest_unread: number
  unread_messages: number

  conversation: any

  // Only keep this if the API actually returns conversation_id.
  conversation_id: PublicId

  created_at: string
  direct_message: number | boolean
  id: PublicId
  private: number | boolean
  settings: any
  updated_at: string

  // Only keep this if the API actually returns user_id.
  user_id: PublicId

  is_muted: boolean
  group: string | null
  active_subscription: boolean
}
interface InboxProps {
  customClass?: string
}
interface ConversationListState {
  cacheKey: string
  conversations: IChatCard[]
}
export const Inbox = ({ customClass }: InboxProps) => {

  const [searchTerm, setSearchTerm] = useState('')
  const [searchText, setSearchText] = useState('')
  const [sortBy, setSortBy] = useState<'most-recent' | 'unread-first' | 'oldest-unread' | 'unread-premium' | 'mark-all-as-read'>('most-recent')

  const [conversationListState, setConversationListState] =
    useState<ConversationListState>({
      cacheKey: '',
      conversations: []
    })

  const allConversations = conversationListState.conversations
  const { setFooterHidden, clearFooterHidden } = useHeaderLayout()
  const { setTitle } = usePageTitle()
  
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const observer = useRef<IntersectionObserver | null>(null)

  // cache-hydrated poll answers / ts so UI can render poll block before network
  const [cachedPollAnswers, setCachedPollAnswers] = useState<any[]>([])
  const [cachedLatestPollAnswerTs, setCachedLatestPollAnswerTs] = useState<string | null>(null)

  const cacheKey = `${searchTerm}-${sortBy}`

  const { t } = useTranslation()
  const navigate = useNavigate()
  const userData = useUserProfile()!
  const queryClient = useQueryClient()
  const modalData = useModalContext()
  const resetMessages = usePendingNotificationsStore(s => s.resetMessages)

  const { id } = useParams<{ id?: string }>()
  const conversationOpen = !!id

  const sortElements = userData?.role === 'model'
    ? {
        first_section: [
          { value: 'most-recent', name: t('showMostRecent') },
          { value: 'unread-first', name: t('showUnreadFirst') },
          { value: 'oldest-unread', name: t('Show Oldest Unread First') },
          { value: 'unread-premium', name: t('Show Unread from Premium Buyers') },
          { value: 'mark-all-as-read', name: t('markAllAsRead') }
        ]
      }
    : {
        first_section: [
          { value: 'most-recent', name: t('showMostRecent') },
          { value: 'unread-first', name: t('showUnreadFirst') },
          { value: 'mark-all-as-read', name: t('markAllAsRead') }
        ]
      }

  const {
    data,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery(
    ['conversations', searchTerm, sortBy],
    async ({ pageParam = 1, signal }) =>
      await getConversations(
        {
          page: pageParam,
          search: searchTerm || null,
          sort: sortBy || null,
        },
        signal
      ),
    {
      enabled: !conversationOpen ||
    window.innerWidth > 965,
      getNextPageParam: (lastPage) => {
        const current = lastPage.meta?.current_page ?? lastPage.current_page
        const last    = lastPage.meta?.last_page   ?? lastPage.last_page
        return current && last && current < last ? current + 1 : undefined
      },
      // don't retry cancellations
      retry: (count, err: any) =>
        err?.code === 'ERR_CANCELED' ? false : count < 3,
    }
  )

    useEffect(() => {
      resetMessages()
    }, [resetMessages])

/*     useEffect(() => {
        setTitle(t('Inbox'))
    }, [setTitle]) */

    // Cancel queries on unmount
    useEffect(() => {
      return () => {
        queryClient.cancelQueries({ queryKey: ['conversations'] })
      }
    }, [queryClient])
    
    const setCache = useInboxCacheStore(s => s.setCache)
    const cachedEntry = useInboxCacheStore(s => {
      const raw = s.conversationsCache[cacheKey]
      if (!raw) return undefined
      return Array.isArray(raw) ? { conversations: raw } : raw
    })


    // ---------- cache hydration (conversations + poll answers) ----------
  useEffect(() => {
    if (cachedEntry) {
      setConversationListState({
        cacheKey,
        conversations: Array.isArray(cachedEntry.conversations)
          ? cachedEntry.conversations
          : []
      })

      setCachedPollAnswers(cachedEntry.poll_answers ?? [])
      setCachedLatestPollAnswerTs(cachedEntry.latestPollAnswerTs ?? null)
    } else {
      setConversationListState({
        cacheKey,
        conversations: []
      })

      setCachedPollAnswers([])
      setCachedLatestPollAnswerTs(null)
    }
  }, [cachedEntry, cacheKey])

  // ----- helpers for parsing various API shapes -----
  const getBody = (page: any) => {
    if (page && typeof page === 'object' && 'poll_answers' in page) return page
    if (page?.data && typeof page.data === 'object' && !Array.isArray(page.data)) return page.data
    return page
  }

  // poll answers: use live data if present; else cached
  const pollAnswersFromFirstPage = useMemo(() => {
    const p1 = data?.pages?.[0]
    if (p1) {
      const body = getBody(p1)
      return body?.poll_answers ?? []
    }
    return cachedPollAnswers
  }, [data, cachedPollAnswers])

  const latestPollAnswerTs = useMemo(() => {
    const all = (pollAnswersFromFirstPage ?? []).flatMap((p: any) => p.answers ?? [])
    if (!all.length) {
      // fall back to cached ts if we had no answers array (or we were on old cache)
      return cachedLatestPollAnswerTs
    }
    return all.reduce((max: string | null, a: any) => {
      if (!max) return a.created_at
      return new Date(a.created_at) > new Date(max) ? a.created_at : max
    }, null as string | null)
  }, [pollAnswersFromFirstPage, cachedLatestPollAnswerTs])

  // merge pages into a single conversations list; then cache conversations + poll metadata
  useEffect(() => {
    if (data && !isLoading) {
      const merged: any[] = data.pages.flatMap((page: any) => {
        const body = getBody(page)
        if (Array.isArray(body)) return body
        if (Array.isArray(body?.data)) return body.data
        if (Array.isArray(body?.conversations)) return body.conversations
        if (Array.isArray(body?.data?.data)) return body.data.data
        return []
      })

      setConversationListState({
      cacheKey,
      conversations: merged
    })

      // use the same poll answers we computed (handles both live or cached path)
      const payload: InboxCacheEntry = {
        conversations: merged,
        poll_answers: pollAnswersFromFirstPage ?? [],
        latestPollAnswerTs: latestPollAnswerTs ?? null,
        ts: Date.now()
      }

      setCache(cacheKey, payload)
    }
  }, [data, isLoading, cacheKey, setCache, pollAnswersFromFirstPage, latestPollAnswerTs])

  // debounce search input -> term
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchTerm(searchText)
    }, 500)
    return () => clearTimeout(timeout)
  }, [searchText])


  /*   const messagesSignal = usePendingNotificationsStore(s => s.messages)

    useEffect(() => {
      // lean: invalidate current query; useInfiniteQuery wiring handles pagination + merge
      queryClient.invalidateQueries(['conversations', searchTerm, sortBy])
    }, [messagesSignal, queryClient, searchTerm, sortBy]) */

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return

    if (conversationOpen) {
      clearFooterHidden('inbox')
      return
    }

    let lastTop = el.scrollTop
    let ticking = false

    const SHOW_AT_TOP_PX = 20
    const HIDE_AFTER_SCROLL_PX = 80
    const DELTA_PX = 8

    const onScroll = () => {
      const top = el.scrollTop
      const delta = top - lastTop

      if (!ticking) {
        ticking = true

        requestAnimationFrame(() => {
          ticking = false

          if (top <= SHOW_AT_TOP_PX) {
            setFooterHidden(false, 'inbox')
            lastTop = top
            return
          }

          if (top < HIDE_AFTER_SCROLL_PX) {
            setFooterHidden(false, 'inbox')
            lastTop = top
            return
          }

          if (Math.abs(delta) < DELTA_PX) {
            lastTop = top
            return
          }

          if (delta > 0) setFooterHidden(true, 'inbox')
          else setFooterHidden(false, 'inbox')

          lastTop = top
        })
      }
    }

    el.addEventListener('scroll', onScroll, { passive: true })
    setFooterHidden(el.scrollTop > HIDE_AFTER_SCROLL_PX, 'inbox')

    return () => {
      el.removeEventListener('scroll', onScroll)
      clearFooterHidden('inbox')
    }
  }, [conversationOpen, setFooterHidden, clearFooterHidden])



  const lastConversationRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (conversationOpen) return
      if (isFetchingNextPage || isLoading) return

      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver(
        entries => {
          const first = entries[0]

          console.log('observer', {
            isIntersecting: first?.isIntersecting,
            hasNextPage,
            isFetchingNextPage,
          })

          if (first?.isIntersecting && hasNextPage) {
            fetchNextPage()
          }
        },
        {
          root: scrollContainerRef.current,
          rootMargin: '150px',
        }
      )

      if (node) observer.current.observe(node)
    },
    [conversationOpen, isFetchingNextPage, isLoading, hasNextPage, fetchNextPage]
  )

const { data: quickChatsData, isLoading: loadingQuickChats } = useQuery(
  'quick-chats',
  getQuickContacts,
  {
    enabled: !!userData.chat_settings?.favorite_contacts,
    staleTime: 60_000,
  }
)

const filteredConversations = useMemo(() => {
  const base = (allConversations ?? []).filter(
    c => searchText ? c.last_message !== null : true
  )

  if (sortBy === 'unread-premium') {
    return base.filter(
      c =>
        c.unread_messages > 0 &&
        c.other_users?.[0]?.is_premium
    )
  }

  return base
}, [
  allConversations,
  searchText,
  sortBy
])

  const isSearchTransitioning =
    conversationListState.cacheKey !== cacheKey

  const isSearchActive =
    searchText.trim().length > 0 ||
    searchTerm.trim().length > 0 ||
    isSearchTransitioning


  const conversationsWithPoll = useMemo(() => {
    if (isSearchActive) {
      return filteredConversations
    }

    if (!latestPollAnswerTs) {
      return filteredConversations
    }

    const pollTs = Date.parse(latestPollAnswerTs)

    const getTs = (conversation: any) =>
      Date.parse(
        conversation?.last_activity_at ??
        conversation.updated_at ??
        conversation.created_at
      )

    let insertAt = filteredConversations.length

    for (let i = 0; i < filteredConversations.length; i++) {
      const curTs = getTs(filteredConversations[i])

      if (sortBy === 'oldest-unread') {
        if (pollTs <= curTs) {
          insertAt = i
          break
        }
      } else if (sortBy === 'unread-first') {
        const unreadBias = filteredConversations[i].unread_messages > 0

        const nextIsReadBlock =
          !unreadBias &&
          (i === 0 ||
            filteredConversations[i - 1].unread_messages > 0)

        if (nextIsReadBlock || pollTs >= curTs) {
          insertAt = i
          break
        }
      } else if (pollTs >= curTs) {
        insertAt = i
        break
      }
    }

    const output = filteredConversations.slice()

    output.splice(insertAt, 0, {
      __kind: 'poll_block'
    } as any)

    return output
  }, [
    filteredConversations,
    latestPollAnswerTs,
    sortBy,
    isSearchActive
  ])

  return (
    <WithHeaderSection
      customClass={customClass}
      ref={scrollContainerRef}
      headerSection={
        <LayoutHeader
          type="search-with-buttons"
          searchValue={searchText}
          searchFn={setSearchText}
          clearFn={() => setSearchText('')}
          additionalProps={{ placeholder: `${t('searchInbox')}` }}
          buttons={[
            { type: 'sort', elements: { ...sortElements } },
            ...(userData.role === 'model'
              ? [
                  { type: 'custom', icon: <spriteIcons.IconGraph />, action: () => modalData.addModal(t('massMessages'), <MassMessagesModal />) },
                  {
                    type: 'custom',
                    customClass: 'schedule-button',
                    icon: <spriteIcons.IconCalendarClockOutline />,
                    action: () =>
                      navigate('/schedule', {
                        state: { scheduleMessages: true }
                      })
                  }                
                ]
              : [])
          ]}
          applyFn={setSortBy}
          sortingProps={{ selectedSort: sortBy, selectedOrder: '' }}
        />
      }
    >
      <div className="messages">
{!!userData.chat_settings?.favorite_contacts && (
  <Swiper
    className="messages__quickContacts"
    slidesPerView="auto"
    spaceBetween={10}
    style={{ marginBottom: '1.8rem' }}
  >
    {loadingQuickChats ? (
      <SwiperSlide style={{ width: 'auto' }}>
        <QuickContact isLoading />
      </SwiperSlide>
    ) : quickChatsData?.data?.length === 0 ? (
      <SwiperSlide style={{ width: 'auto' }}>
        <QuickContact isPlaceholder />
      </SwiperSlide>
    ) : (
      quickChatsData?.data?.map(
        (
          user: {
            amount_of_messages: number
            conversation_id: string
            other_user_id: number
            display_name: string
            avatar: string
          },
          index: number
        ) => (
          <SwiperSlide
            key={user.other_user_id ?? index}
            style={{ width: 'auto' }}
          >
            <QuickContact user={user} />
          </SwiperSlide>
        )
      )
    )}
  </Swiper>
)}
<div className="messages__list">
  {!isLoading && allConversations.length === 0 && !isSearchActive ? (
    <PlaceholderMobile />
  ) : (
    conversationsWithPoll.map((item: any, index: number) =>
      item?.__kind === 'poll_block'
        ? (
          <PollAnswers
            key="poll-block"
            pollAnswers={pollAnswersFromFirstPage}
          />
        )
        : (
          <ChatCard
            key={item?.id ?? index}
            chat={item}
          />
        )
    )
  )}

  {(isLoading || hasNextPage) && (
    <div
      className="loader"
      ref={lastConversationRef}
    />
  )}
</div>

        {userData?.role === 'fan' ? (
          <button
            type="button"
            className="messages__addNew"
            onClick={() => {
              modalData.addModal(
                t('sendANewMessage'),
                <WhoAreYouWritingToFan closeFn={modalData.clearModal} />,
                false, // withoutTitle
                false,   // withoutCloseIcon 
              )
            }}
          >
            <img src={AllIcons.chat_plus} alt={t('addNew')} />
          </button>
        ) : (
<Link to="/messages/new" className="messages__addNew">
  <img src={AllIcons.chat_plus} alt={t('addNew')} />
</Link>
        )}
      </div>
    </WithHeaderSection>
  )
}
